import * as t from 'io-ts'
import * as E from 'fp-ts/Either'
import { pipe } from 'fp-ts/function'
import * as crypto from 'crypto'

/**
 * Branded type for Session ID
 * Format: NNN-NNNNNNN-NNNNNNN where each N is a number
 * See [https://w.amazon.com/bin/view/Weblab/WeblabAllocationProvider]
 */

interface SessionIDBrand {
  readonly SessionID: unique symbol
}

const SessionIDCodec = t.brand(t.string, (value): value is t.Branded<string, SessionIDBrand> => /^\d{3}-\d{7}-\d{7}$/.test(value), 'SessionID')

export type SessionID = t.TypeOf<typeof SessionIDCodec>

const formatSessionId = (part1: number, part2: number, part3: number): string => {
  const formattedPart1 = part1.toString().padStart(3, '0')
  const formattedPart2 = part2.toString().padStart(7, '0')
  const formattedPart3 = part3.toString().padStart(7, '0')

  return `${formattedPart1}-${formattedPart2}-${formattedPart3}`
}

// Helper functions for generating Session Ids
export const convertToSessionId = (value: string): SessionID => {
  return pipe(
    SessionIDCodec.decode(value),
    E.getOrElse<t.Errors, SessionID>(() => {
      throw new Error(`Invalid SessionID format: ${value}`)
    }),
  )
}

export const generateSessionID = (seed: string): SessionID => {
  const hash = crypto
    .createHash('sha256')
    .update(seed)
    .digest('hex')

  const part1 = parseInt(hash.substring(0, 6), 16) % 1_000
  const part2 = parseInt(hash.substring(6, 16), 16) % 10_000_000
  const part3 = parseInt(hash.substring(16, 26), 16) % 10_000_000

  const sessionId = formatSessionId(part1, part2, part3)
  return convertToSessionId(sessionId)
}

export const generateRandomSessionID = (): SessionID => {
  const part1 = Math.floor(Math.random() * 1_000)
  const part2 = Math.floor(Math.random() * 10_000_000)
  const part3 = Math.floor(Math.random() * 10_000_000)

  const sessionId = formatSessionId(part1, part2, part3)
  return convertToSessionId(sessionId)
}
